import { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, IconButton,
  TextField, Checkbox, Divider, CircularProgress, Switch, Chip, FormControlLabel, Radio,
  RadioGroup, Table, TableHead, TableBody, TableRow, TableCell, LinearProgress, Alert, Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import useStore from '../../config/store';
import useAtsSenderName from '../../hooks/useAtsSenderName';
import useWhatsappChannel from '../../hooks/useWhatsappChannel';
import { formatDate } from '../../erp/formatDate';

/**
 * Solicitud masiva de documentos (ATS, PLAN_SOLICITUD_DOCUMENTOS_MASIVA, 2026-10-14).
 * Mismo modal para las dos entradas:
 *  - mode 'column': botón del módulo "Solicitud de documentos" de la columna.
 *    Abre en "Estado" (avance de cada candidato de la etapa, "Ver ficha",
 *    "Recordar a pendientes") y desde ahí "Nueva solicitud".
 *  - mode 'selection': "Solicitar documentos (n)" en la barra de selección del
 *    kanban. Abre directo en "Nueva solicitud" con los candidatos marcados.
 * Envía uno por uno (como los tests y "Verificar pendientes") con
 * POST /applications/:id/request-documents en modo 'merge': se suma a lo ya
 * pedido y no se pierde nada de lo subido. Los documentos nuevos llevan un id
 * propio (uuid) para que el backend los cree una sola vez en el catálogo.
 */

// Fuera de las etapas (descartado, bloqueado, por validar) no se envía nada
const OUTSIDE_STATUSES = ['discarded', 'blocked', 'pending_validation'];
// Errores que valen para todos los que faltan: detienen el envío
const STOP_CODES = ['TIER_LIMIT', 'NOT_CONFIGURED'];
const FILTERS = ['all', 'none', 'pending', 'complete'];
const ACCENT = '#2563EB';

const appIdOf = (a) => a.uid || a.id;
const itemsOf = (a) => (Array.isArray(a?.documentRequest?.items) ? a.documentRequest.items : []);
const pendingOf = (a) => itemsOf(a).filter((it) => it.status !== 'uploaded');

/** Avance de la solicitud de documentos: state 'none' | 'pending' | 'complete' */
export const documentsProgress = (application) => {
  const items = itemsOf(application);
  const uploaded = items.filter((it) => it.status === 'uploaded').length;
  const total = items.length;
  let state = 'none';
  if (total > 0) state = uploaded === total ? 'complete' : 'pending';
  return { total, uploaded, state };
};

export const PROGRESS_TONES = {
  none: { bg: '#F3F4F6', color: '#374151' },
  pending: { bg: '#EFF6FF', color: '#1D4ED8' },
  complete: { bg: '#DCFCE7', color: '#166534' },
};

const AtsBulkRequestDocumentsDialog = ({
  open,
  onClose,
  mode = 'column',
  stageName = '',
  applications = [],
  positionId,
  onApplicationPatch,
  onOpenApplication,
  onFinished,
}) => {
  const { t } = useTranslation();
  const { organization, project, authToken, showAlert } = useStore.getState();
  const tb = (key, opts) => t(`ats.documents.bulk.${key}`, opts);
  const headers = { Authorization: `Bearer ${authToken}` };

  // Candidatos con los que se abrió: la selección del kanban puede cambiar después
  const [pool, setPool] = useState([]);
  // Solicitud más reciente por postulación (recarga al abrir + respuestas del envío)
  const [docOverrides, setDocOverrides] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState('status'); // status | create | remind | sending | result
  const [filter, setFilter] = useState('all');

  const [catalog, setCatalog] = useState([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [sel, setSel] = useState({});          // { [id]: { required } } — presencia = seleccionado
  const [customDocs, setCustomDocs] = useState([]); // [{ id, label, instructions, required }]
  const [newLabel, setNewLabel] = useState('');
  const [newInstructions, setNewInstructions] = useState('');
  const [newRequired, setNewRequired] = useState(false);
  const [channel, setChannel] = useState('email');
  const [selectedIds, setSelectedIds] = useState([]);
  const { senderName, setSenderName } = useAtsSenderName(open);
  // Sin WhatsApp en la organización (sin número propio ni el compartido permitido)
  const whatsapp = useWhatsappChannel(open);
  useEffect(() => {
    if (!whatsapp.usable && channel === 'whatsapp') setChannel('email');
  }, [whatsapp.usable, channel]);

  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState(null); // { kind, rows: [{ applicationId, name, outcome, message }], stopped }
  // Hubo al menos un envío: al cerrar, la barra de selección se limpia
  const [sentAny, setSentAny] = useState(false);

  const rows = useMemo(
    () => pool.map((a) => {
      const id = appIdOf(a);
      return Object.prototype.hasOwnProperty.call(docOverrides, id)
        ? { ...a, documentRequest: docOverrides[id] }
        : a;
    }),
    [pool, docOverrides]
  );

  const canReceive = (a) => channel !== 'email' || !!a.candidateEmail;

  // Estado real de los documentos (los candidatos suben por su cuenta): se
  // recarga al abrir y con "Actualizar", y se lleva a las tarjetas del kanban
  const refreshDocuments = async (list) => {
    if (!positionId || !list || list.length === 0) return;
    setRefreshing(true);
    try {
      const res = await api.get('/applications', { params: { positionId }, headers });
      const fresh = res.data?.response?.data || [];
      const byId = new Map(list.map((a) => [appIdOf(a), a]));
      const next = {};
      fresh.forEach((a) => {
        const id = appIdOf(a);
        if (!byId.has(id)) return;
        const documentRequest = a.documentRequest || null;
        next[id] = documentRequest;
        const before = byId.get(id).documentRequest || null;
        if (onApplicationPatch && JSON.stringify(before) !== JSON.stringify(documentRequest)) {
          onApplicationPatch(id, { documentRequest });
        }
      });
      setDocOverrides((prev) => ({ ...prev, ...next }));
    } catch (err) {
      // Sin recarga se muestra lo que ya tiene el kanban
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  const loadCatalog = async () => {
    if (catalogLoaded || loadingCatalog) return;
    setLoadingCatalog(true);
    try {
      const res = await api.get(
        `/projects/document-catalog?organizationId=${organization}&projectId=${project}`,
        { headers }
      );
      const cat = res.data?.response?.data || [];
      setCatalog(Array.isArray(cat) ? cat : []);
      setCatalogLoaded(true);
    } catch (err) {
      console.error(err);
      showAlert(t('ats.documents.loadError'), 'error');
    } finally {
      setLoadingCatalog(false);
    }
  };

  // El catálogo se carga con el efecto de más abajo al entrar a "Nueva solicitud"
  const goCreate = (ids) => {
    setSelectedIds(ids);
    setView('create');
  };

  const goRemind = (ids) => {
    setSelectedIds(ids);
    setView('remind');
  };

  useEffect(() => {
    if (!open) return;
    const initial = (applications || []).filter((a) => !OUTSIDE_STATUSES.includes(a.status));
    setPool(initial);
    setDocOverrides({});
    setFilter('all');
    setSel({});
    setCustomDocs([]);
    setNewLabel('');
    setNewInstructions('');
    setNewRequired(false);
    setChannel('email');
    setResults(null);
    setSentAny(false);
    setProgress({ done: 0, total: 0 });
    setCatalog([]);
    setCatalogLoaded(false);
    if (mode === 'selection') {
      setSelectedIds(initial.map(appIdOf));
      setView('create');
    } else {
      setSelectedIds([]);
      setView('status');
    }
    refreshDocuments(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Catálogo del proyecto: al entrar a "Nueva solicitud" (en selección, al abrir)
  useEffect(() => {
    if (open && view === 'create' && !catalogLoaded) loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view, catalogLoaded]);

  const toggleCatalog = (id) => {
    setSel((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = { required: false };
      return next;
    });
  };

  const addCustomDoc = () => {
    const label = newLabel.trim();
    if (!label) return;
    setCustomDocs((prev) => [
      ...prev,
      // Id propio (uuid): el backend lo usa tal cual y lo crea una sola vez en el catálogo
      { id: uuidv4(), label, instructions: newInstructions.trim(), required: newRequired },
    ]);
    setNewLabel('');
    setNewInstructions('');
    setNewRequired(false);
  };

  const documentsToSend = [
    ...catalog
      .filter((c) => sel[c.id])
      .map((c) => ({ id: c.id, label: c.label, instructions: c.instructions || '', required: !!sel[c.id].required })),
    ...customDocs.map((d) => ({ id: d.id, label: d.label, instructions: d.instructions || '', required: !!d.required })),
  ];

  const toggleCandidate = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Envío secuencial, una llamada por postulación
  const runBatch = async (kind, targets, buildDocuments) => {
    if (targets.length === 0) return;
    setView('sending');
    setProgress({ done: 0, total: targets.length });
    const out = [];
    let stopped = null;
    for (let i = 0; i < targets.length; i++) {
      const app = targets[i];
      const id = appIdOf(app);
      const name = app.candidateName || app.candidateEmail || '—';
      if (stopped) {
        out.push({ applicationId: id, name, outcome: 'failed', message: stopped });
      } else {
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await api.post(
            `/applications/${id}/request-documents`,
            { documents: buildDocuments(app), channel, senderName, mode: 'merge' },
            { headers }
          );
          const data = res.data?.response?.data || {};
          if (Array.isArray(data.items)) {
            const documentRequest = {
              ...(app.documentRequest || {}),
              items: data.items,
              ...(data.requestedAt ? { requestedAt: data.requestedAt } : {}),
            };
            setDocOverrides((prev) => ({ ...prev, [id]: documentRequest }));
            if (onApplicationPatch) onApplicationPatch(id, { documentRequest });
          }
          if (data.reason === 'NO_PENDING') out.push({ applicationId: id, name, outcome: 'already' });
          else if (data.sent === false || data.emailSent === false) out.push({ applicationId: id, name, outcome: 'failed', message: tb('emailFailed') });
          else out.push({ applicationId: id, name, outcome: 'sent' });
        } catch (err) {
          console.error(err);
          const r = err.response?.data?.response || {};
          const message = r.message || err.response?.data?.errorMessage || t('ats.documents.sendError');
          if (STOP_CODES.includes(r.code)) stopped = message;
          out.push({ applicationId: id, name, outcome: 'failed', message });
        }
      }
      setProgress({ done: i + 1, total: targets.length });
    }
    if (out.some((r) => r.outcome === 'sent')) setSentAny(true);
    setResults({ kind, rows: out, stopped });
    setView('result');
  };

  const handleClose = () => {
    if (view === 'sending') return;
    if (sentAny && onFinished) onFinished();
    onClose();
  };

  const retryFailed = () => {
    const failedIds = (results?.rows || []).filter((r) => r.outcome === 'failed').map((r) => r.applicationId);
    const kind = results?.kind;
    setResults(null);
    if (kind === 'remind') goRemind(failedIds);
    else goCreate(failedIds);
  };

  // ── Vistas ──────────────────────────────────────────────────────────────
  const matchesFilter = (a, f) => f === 'all' || documentsProgress(a).state === f;
  const visibleRows = rows.filter((a) => matchesFilter(a, filter));
  const remindRows = rows.filter((a) => pendingOf(a).length > 0);
  const createTargets = rows.filter((a) => selectedIds.includes(appIdOf(a)) && canReceive(a));
  const remindTargets = remindRows.filter((a) => selectedIds.includes(appIdOf(a)) && canReceive(a));

  const progressChip = (a) => {
    const p = documentsProgress(a);
    let label = tb('stateNone');
    if (p.state === 'complete') label = tb('stateComplete');
    else if (p.state === 'pending') label = tb('stateProgress', { uploaded: p.uploaded, total: p.total });
    const chip = (
      <Chip
        size="small"
        label={label}
        sx={{ backgroundColor: PROGRESS_TONES[p.state].bg, color: PROGRESS_TONES[p.state].color, fontWeight: 700, height: 22, fontSize: 11 }}
      />
    );
    if (p.total === 0) return chip;
    return (
      <Tooltip
        arrow
        title={(
          <Box>
            {itemsOf(a).map((it) => (
              <Typography key={it.docId} variant="caption" sx={{ display: 'block' }}>
                {`${it.label}${it.required ? ' *' : ''}: ${it.status === 'uploaded' ? t('ats.documents.uploaded') : t('ats.documents.pending')}`}
              </Typography>
            ))}
          </Box>
        )}
      >
        {chip}
      </Tooltip>
    );
  };

  const sectionTitle = (text) => (
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
      {text}
    </Typography>
  );

  const renderStatus = () => (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">{tb('statusHelp')}</Typography>
        <Tooltip title={tb('refresh')}>
          <span>
            <IconButton size="small" onClick={() => refreshDocuments(rows)} disabled={refreshing} aria-label={tb('refresh')}>
              {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{tb('emptyStage')}</Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
            {FILTERS.map((f) => {
              const active = filter === f;
              const count = rows.filter((a) => matchesFilter(a, f)).length;
              return (
                <Chip
                  key={f}
                  size="small"
                  label={tb(`filter.${f}`, { count })}
                  variant={active ? 'filled' : 'outlined'}
                  onClick={() => setFilter(f)}
                  sx={active ? { backgroundColor: ACCENT, color: '#fff', fontWeight: 600 } : { fontWeight: 500 }}
                />
              );
            })}
          </Box>
          {visibleRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{tb('emptyFilter')}</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{tb('colCandidate')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{tb('colDocuments')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{tb('colRequested')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((a) => (
                  <TableRow key={appIdOf(a)}>
                    <TableCell>
                      <Typography variant="body2">{a.candidateName || '—'}</Typography>
                      {a.candidateEmail && (
                        <Typography variant="caption" color="text.secondary">{a.candidateEmail}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{progressChip(a)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {a.documentRequest?.requestedAt ? formatDate(a.documentRequest.requestedAt) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {onOpenApplication && (
                        <Button
                          size="small"
                          startIcon={<OpenIcon fontSize="small" />}
                          onClick={() => { handleClose(); onOpenApplication(a); }}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {tb('viewCandidate')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </>
  );

  // Lista de candidatos con casillas (Nueva solicitud y Recordar)
  const renderCandidateList = (list, { showMissing = false } = {}) => {
    const receivable = list.filter(canReceive);
    const receivableIds = receivable.map(appIdOf);
    const selectedCount = receivableIds.filter((id) => selectedIds.includes(id)).length;
    const allSelected = receivable.length > 0 && selectedCount === receivable.length;
    return (
      <>
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={selectedCount > 0 && !allSelected}
              disabled={receivable.length === 0}
              onChange={() => setSelectedIds((prev) => (allSelected
                ? prev.filter((id) => !receivableIds.includes(id))
                : Array.from(new Set([...prev, ...receivableIds]))))}
            />
          )}
          label={<Typography variant="body2" fontWeight={600}>{tb('selectAll')}</Typography>}
        />
        <Box sx={{ maxHeight: 280, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {list.map((a) => {
            const id = appIdOf(a);
            const ok = canReceive(a);
            let detail = a.candidateEmail || '';
            if (!ok) detail = tb('noEmail');
            else if (showMissing) detail = tb('missing', { docs: pendingOf(a).map((it) => it.label).join(', ') });
            return (
              <Box
                key={id}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                  px: 1, py: 0.25, borderBottom: '1px solid', borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <FormControlLabel
                  sx={{ minWidth: 0, mr: 0, flex: 1 }}
                  control={(
                    <Checkbox
                      size="small"
                      checked={ok && selectedIds.includes(id)}
                      onChange={() => toggleCandidate(id)}
                      disabled={!ok}
                    />
                  )}
                  label={(
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{a.candidateName || a.candidateEmail || '—'}</Typography>
                      {detail && (
                        <Typography variant="caption" color={ok ? 'text.secondary' : 'error'} noWrap sx={{ display: 'block' }}>
                          {detail}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
                {!showMissing && <Box sx={{ flexShrink: 0 }}>{progressChip(a)}</Box>}
              </Box>
            );
          })}
        </Box>
      </>
    );
  };

  const renderChannel = () => (
    <>
      {sectionTitle(t('ats.documents.channelTitle'))}
      <RadioGroup row value={channel} onChange={(e) => setChannel(e.target.value)} sx={{ mb: 0.5 }}>
        <FormControlLabel
          value="email"
          control={<Radio size="small" />}
          label={<Typography variant="body2">{t('ats.documents.channelEmail')}</Typography>}
        />
        <FormControlLabel
          value="whatsapp"
          control={<Radio size="small" />}
          disabled={!whatsapp.usable}
          label={<Typography variant="body2">{t('ats.documents.channelWhatsapp')}</Typography>}
        />
      </RadioGroup>
      {!whatsapp.usable && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('ats.documents.channelWhatsappUnavailable')}
        </Typography>
      )}
      {channel === 'whatsapp' ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {tb('whatsappHint')}
        </Typography>
      ) : (
        <TextField
          size="small"
          fullWidth
          label={t('ats.config.emailSender.fieldLabel')}
          value={senderName}
          onChange={(e) => setSenderName(e.target.value)}
          inputProps={{ maxLength: 80 }}
          sx={{ mb: 2, mt: 0.5 }}
        />
      )}
    </>
  );

  const renderCreate = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{tb('documentsHelp')}</Typography>
      {sectionTitle(t('ats.documents.catalogTitle'))}
      {loadingCatalog && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {!loadingCatalog && catalog.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('ats.documents.catalogEmpty')}</Typography>
      )}
      {!loadingCatalog && catalog.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
          {catalog.map((c) => {
            const isSel = !!sel[c.id];
            return (
              <Box key={c.id} sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 0.5 }}>
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', mr: 0 }}
                  control={<Checkbox checked={isSel} onChange={() => toggleCatalog(c.id)} size="small" />}
                  label={(
                    <Box sx={{ pt: 0.5 }}>
                      <Typography variant="body2">{c.label}</Typography>
                      {c.instructions ? (
                        <Typography variant="caption" color="text.secondary">{c.instructions}</Typography>
                      ) : null}
                    </Box>
                  )}
                />
                {isSel && (
                  <Box sx={{ pl: 4, pb: 0.5 }}>
                    <FormControlLabel
                      control={(
                        <Switch
                          size="small"
                          checked={!!sel[c.id].required}
                          onChange={(e) => setSel((prev) => ({ ...prev, [c.id]: { required: e.target.checked } }))}
                        />
                      )}
                      label={<Typography variant="caption">{t('ats.documents.required')}</Typography>}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {customDocs.length > 0 && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
            {t('ats.documents.newDocsTitle')}
          </Typography>
          {customDocs.map((d) => (
            <Box
              key={d.id}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: '1px solid', borderColor: 'divider', py: 0.5,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2">{d.label}</Typography>
                {d.instructions ? (
                  <Typography variant="caption" color="text.secondary">{d.instructions}</Typography>
                ) : null}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <FormControlLabel
                  control={(
                    <Switch
                      size="small"
                      checked={d.required}
                      onChange={(e) => setCustomDocs((prev) => prev.map((x) => (x.id === d.id ? { ...x, required: e.target.checked } : x)))}
                    />
                  )}
                  label={<Typography variant="caption">{t('ats.documents.required')}</Typography>}
                />
                <IconButton
                  size="small"
                  onClick={() => setCustomDocs((prev) => prev.filter((x) => x.id !== d.id))}
                  aria-label={tb('removeDoc')}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1, mt: 1 }}>
        {t('ats.documents.addNewTitle')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          size="small"
          label={t('ats.documents.labelField')}
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          fullWidth
          placeholder={t('ats.documents.labelPlaceholder')}
        />
        <TextField
          size="small"
          label={t('ats.documents.instructionsField')}
          value={newInstructions}
          onChange={(e) => setNewInstructions(e.target.value)}
          fullWidth
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <FormControlLabel
            control={<Switch size="small" checked={newRequired} onChange={(e) => setNewRequired(e.target.checked)} />}
            label={<Typography variant="caption">{t('ats.documents.required')}</Typography>}
          />
          <Button size="small" startIcon={<AddIcon />} onClick={addCustomDoc} disabled={!newLabel.trim()}>
            {t('ats.documents.addButton')}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />
      {renderChannel()}
      {sectionTitle(tb('candidatesTitle'))}
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{tb('noCandidates')}</Typography>
      ) : renderCandidateList(rows)}
    </>
  );

  const renderRemind = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{tb('remindHelp')}</Typography>
      {renderChannel()}
      {sectionTitle(tb('candidatesTitle'))}
      {remindRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{tb('remindNone')}</Typography>
      ) : renderCandidateList(remindRows, { showMissing: true })}
    </>
  );

  const renderSending = () => (
    <Box sx={{ py: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {tb('sending', { done: progress.done, total: progress.total })}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={progress.total ? (progress.done * 100) / progress.total : 0}
        sx={{ height: 8, borderRadius: 4, mb: 1 }}
      />
      <Typography variant="caption" color="text.secondary">{tb('sendingHint')}</Typography>
    </Box>
  );

  const renderResult = () => {
    const list = results?.rows || [];
    const sent = list.filter((r) => r.outcome === 'sent').length;
    const already = list.filter((r) => r.outcome === 'already').length;
    const failed = list.filter((r) => r.outcome === 'failed');
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {results?.stopped && <Alert severity="error">{tb('resultStopped', { message: results.stopped })}</Alert>}
        {sent > 0 && <Alert severity="success">{tb('resultSent', { count: sent })}</Alert>}
        {already > 0 && <Alert severity="info">{tb('resultAlready', { count: already })}</Alert>}
        {failed.length > 0 && (
          <Alert severity="warning">
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              {tb('resultFailed', { count: failed.length })}
            </Typography>
            {failed.map((r) => (
              <Typography key={r.applicationId} variant="caption" sx={{ display: 'block' }}>
                {`• ${r.name}: ${r.message}`}
              </Typography>
            ))}
          </Alert>
        )}
      </Box>
    );
  };

  let title = tb('title');
  if (view === 'create') title = tb('newRequestTitle');
  else if (view === 'remind') title = tb('remindTitle');
  else if (mode === 'column' && stageName) title = tb('titleStage', { stage: stageName });

  const sendCreate = () => {
    if (documentsToSend.length === 0) {
      showAlert(t('ats.documents.selectAtLeastOne'), 'warning');
      return;
    }
    const docs = documentsToSend;
    runBatch('create', createTargets, () => docs);
  };

  const sendRemind = () => {
    runBatch('remind', remindTargets, (a) => pendingOf(a).map((it) => ({
      id: it.docId,
      label: it.label,
      instructions: it.instructions || '',
      required: !!it.required,
    })));
  };

  const hasFailed = (results?.rows || []).some((r) => r.outcome === 'failed');

  return (
    <Dialog open={open} onClose={view === 'sending' ? undefined : handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700 }}>
        {title}
        <IconButton onClick={handleClose} size="small" disabled={view === 'sending'} aria-label={tb('close')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {view === 'status' && renderStatus()}
        {view === 'create' && renderCreate()}
        {view === 'remind' && renderRemind()}
        {view === 'sending' && renderSending()}
        {view === 'result' && renderResult()}
      </DialogContent>
      <DialogActions>
        {view === 'status' && (
          <>
            <Button
              variant="outlined"
              onClick={() => goRemind(remindRows.map(appIdOf))}
              disabled={remindRows.length === 0}
              sx={{ mr: 'auto', borderColor: ACCENT, color: ACCENT }}
            >
              {tb('remindButton', { count: remindRows.length })}
            </Button>
            <Button onClick={handleClose}>{tb('close')}</Button>
            <Button
              variant="contained"
              onClick={() => goCreate([])}
              disabled={rows.length === 0}
              sx={{ backgroundColor: ACCENT, '&:hover': { backgroundColor: '#1D4ED8' } }}
            >
              {tb('newRequest')}
            </Button>
          </>
        )}
        {(view === 'create' || view === 'remind') && (
          <>
            <Button onClick={mode === 'column' ? () => setView('status') : handleClose}>
              {mode === 'column' ? tb('back') : t('ats.documents.cancel')}
            </Button>
            {view === 'create' ? (
              <Button
                variant="contained"
                onClick={sendCreate}
                disabled={loadingCatalog || refreshing || documentsToSend.length === 0 || createTargets.length === 0}
                sx={{ backgroundColor: ACCENT, '&:hover': { backgroundColor: '#1D4ED8' } }}
              >
                {tb('send', { count: createTargets.length })}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={sendRemind}
                disabled={refreshing || remindTargets.length === 0}
                sx={{ backgroundColor: ACCENT, '&:hover': { backgroundColor: '#1D4ED8' } }}
              >
                {tb('remindSend', { count: remindTargets.length })}
              </Button>
            )}
          </>
        )}
        {view === 'result' && (
          <>
            {hasFailed && (
              <Button onClick={retryFailed} sx={{ mr: 'auto' }}>{tb('retryFailed')}</Button>
            )}
            {mode === 'column' && (
              <Button onClick={() => { setResults(null); setView('status'); }}>{tb('viewStatus')}</Button>
            )}
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{ backgroundColor: ACCENT, '&:hover': { backgroundColor: '#1D4ED8' } }}
            >
              {tb('close')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AtsBulkRequestDocumentsDialog;
